import { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, ImageUp, Video, X, Aperture } from "lucide-react";

export default function VisualSearch() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [camError, setCamError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const stopCam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  };

  const startCam = async () => {
    setCamError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError("This browser doesn't support live camera. Please upload a photo instead.");
      return;
    }
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      setCamOn(true);
    } catch (e) {
      setCamError("Camera access was blocked or no camera was found. You can upload a photo instead.");
      toast.error("Couldn't access the camera");
    }
  };

  useEffect(() => {
    if (camOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [camOn]);

  useEffect(() => () => stopCam(), []);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return toast.error("Capture failed, try again");
      onFile(new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" }));
      stopCam();
      toast.success("Photo captured");
    }, "image/jpeg", 0.9);
  };

  const search = async () => {
    if (!file) return toast.error("Take or choose a jewellery photo first");
    setLoading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post("/ai/visual-search", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      // Backend now returns `no_close_matches:true` when nothing clears
      // the similarity threshold — we do NOT show a "latest designs"
      // fallback anymore because it hid how weak the visual match was.
      if (data.no_close_matches) {
        toast.info("No close matches — try a clearer photo or a different angle");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const attrs = result?.attributes || {};
  const attrChips = [
    ["Type", attrs.jewellery_type],
    ["Category", attrs.category],
    ["Style", attrs.style],
    ["Stones", attrs.stones],
    ["Metal", attrs.metal],
  ].filter(([, v]) => v && String(v).toLowerCase() !== "unknown");

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
      <div className="rounded-lg border border-border bg-slate-950 text-white p-8 relative overflow-hidden mb-8">
        <div className="absolute inset-0 mesh-grid opacity-30" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary border border-primary/30 rounded-full px-3 py-1 mb-4">
            <Sparkles className="h-3.5 w-3.5" /> AI Visual Search
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Search by photo</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">Snap a photo with your camera or upload one — our AI reads its type, style, stones and metal, then finds the closest CAD design files on Jeweltix. Works on mobile and desktop webcams.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-8">
        <div className="space-y-4">
          {camOn ? (
            <div className="border-2 border-primary rounded-lg overflow-hidden bg-black relative" data-testid="camera-panel">
              <video ref={videoRef} playsInline muted className="w-full h-64 object-cover" data-testid="camera-video" />
              <button onClick={stopCam} data-testid="camera-close" className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80">
                <X className="h-4 w-4" />
              </button>
              <button onClick={capture} data-testid="camera-capture-btn" className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 font-semibold hover:bg-primary/90">
                <Aperture className="h-4 w-4" /> Capture photo
              </button>
            </div>
          ) : (
            <label className="block border-2 border-dashed border-border rounded-lg p-6 bg-card text-center cursor-pointer hover:border-primary transition-colors">
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-56 object-cover rounded-md" data-testid="search-preview" />
              ) : (
                <div className="py-10 text-muted-foreground">
                  <ImageUp className="h-10 w-10 mx-auto mb-2 text-primary" />
                  <p className="text-sm font-medium">Drop or choose a jewellery photo</p>
                  <p className="text-xs">JPG / PNG / WEBP</p>
                </div>
              )}
              <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="visual-search-file" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
            </label>
          )}

          {!camOn && (
            <button onClick={startCam} data-testid="use-camera-btn" className="w-full flex items-center justify-center gap-2 border border-border rounded-md py-2.5 font-semibold text-sm hover:border-primary hover:text-primary transition-colors">
              <Video className="h-4 w-4" /> Use camera instead
            </button>
          )}
          {camError && <p className="text-xs text-red-500" data-testid="camera-error">{camError}</p>}

          <button onClick={search} disabled={loading || !file} data-testid="visual-search-btn" className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-3 font-semibold hover:bg-primary/90 disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {loading ? "Analysing…" : "Find matching CAD files"}
          </button>

          {attrChips.length > 0 && (
            <div className="border border-border rounded-md p-4 bg-card">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Detected by AI</p>
              <div className="flex flex-wrap gap-2">
                {attrChips.map(([k, v]) => (
                  <span key={k} className="font-mono text-xs bg-secondary px-2.5 py-1 rounded" data-testid="ai-attr"><b>{k}:</b> {v}</span>
                ))}
              </div>
              {attrs.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {attrs.keywords.map((k) => <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">#{k}</span>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          {!result ? (
            <div className="border border-dashed border-border rounded-md py-28 text-center text-muted-foreground">Your matches will appear here.</div>
          ) : result.no_close_matches ? (
            <div
              className="border border-dashed border-amber-500/40 bg-amber-500/5 rounded-md py-16 px-6 text-center"
              data-testid="visual-no-close-matches"
            >
              <p className="font-display text-lg font-bold text-amber-700 mb-2">No close matches</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                We ranked all {result.candidate_count?.toLocaleString?.() || result.candidate_count} listings against your photo but none scored above the similarity threshold ({(result.threshold * 100).toFixed(0)}%). Try a clearer photo, a different angle, or browse the marketplace.
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-display font-bold text-xl mb-4" data-testid="visual-results-heading">
                {result.matches.length} matching design{result.matches.length === 1 ? "" : "s"}
                {result.top_similarity && (
                  <span className="text-sm text-muted-foreground font-normal ml-2">
                    · best match {(result.top_similarity * 100).toFixed(0)}%
                  </span>
                )}
              </h2>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {result.matches.map((l) => (
                  <div key={l.id} className="relative" data-testid={`visual-match-${l.id}`}>
                    {typeof l.similarity === "number" && (
                      <span
                        className="absolute z-10 top-2 left-2 text-[11px] font-mono font-bold px-2 py-1 rounded-full bg-primary text-primary-foreground shadow"
                        data-testid={`match-similarity-${l.id}`}
                      >
                        {(l.similarity * 100).toFixed(0)}% match
                      </span>
                    )}
                    <ListingCard listing={l} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
